import React, { useMemo } from 'react'
import {
	DndContext,
	closestCenter,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
	type DragEndEvent,
} from '@dnd-kit/core'
import {
	arrayMove,
	SortableContext,
	sortableKeyboardCoordinates,
	useSortable,
	verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { TableBody, TableCell, TableRow } from 'components/ui/table'
import { GripVertical } from 'lucide-react'
import { groupTasksByDay, type TaskLike } from 'utils/tasks'
import { cn } from 'lib/utils'

/**
 * Shared sortable TableBody used by the four task list views (TasksCN_Index,
 * ProjectTasksCN, PortfolioTasksCN, LocationTasksCN). Renders tasks bucketed
 * by their `dateDue` day, with a separate `SortableContext` per bucket so
 * cross-day drag-and-drop is structurally impossible (cross-day moves live on
 * the calendar view).
 *
 * Drag is enabled only when the parent's column sort is the default `dateDue`.
 * When the user sorts by another column the grip handles dim and `useSortable`
 * is disabled.
 */

interface SortableTasksTableBodyProps<T extends TaskLike> {
	tasks: T[]
	columnCount: number
	orderBy: string | null
	renderRowCells: (task: T) => React.ReactNode
	rowClassName?: (task: T) => string
	onRowClick?: (task: T) => void
	onReorder: (orderedTaskIds: string[]) => void
	// Per-row gate. Returning false dims the grip and disables useSortable for
	// that task only -- used by PortfolioTasksCN to exclude portfolio-level
	// tasks (different collection) from the per-day reorder.
	isRowDraggable?: (task: T) => boolean
}

interface SortableTaskRowProps<T extends TaskLike> {
	task: T
	dragEnabled: boolean
	rowClassName?: string
	onClick?: () => void
	children: React.ReactNode
}

function SortableTaskRow<T extends TaskLike>({
	task,
	dragEnabled,
	rowClassName,
	onClick,
	children,
}: SortableTaskRowProps<T>) {
	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
		id: task._id,
		disabled: !dragEnabled,
	})

	const style: React.CSSProperties = {
		transform: CSS.Transform.toString(transform),
		transition,
		zIndex: isDragging ? 10 : undefined,
		opacity: isDragging ? 0.85 : 1,
		backgroundColor: isDragging ? 'rgb(239 246 255)' : undefined,
	}

	return (
		<TableRow
			ref={setNodeRef}
			style={style}
			onClick={onClick}
			className={cn(rowClassName, isDragging && 'shadow-md')}
		>
			<TableCell className="w-8 align-middle" onClick={e => e.stopPropagation()}>
				<div
					{...attributes}
					{...listeners}
					className={cn(
						'flex items-center justify-center',
						dragEnabled
							? 'cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600'
							: 'opacity-30 pointer-events-none',
					)}
					title={dragEnabled ? 'Drag to reorder' : 'Sort by Due Date to enable reordering'}
				>
					<GripVertical className="w-4 h-4" />
				</div>
			</TableCell>
			{children}
		</TableRow>
	)
}

function SortableTasksTableBody<T extends TaskLike>({
	tasks,
	columnCount,
	orderBy,
	renderRowCells,
	rowClassName,
	onRowClick,
	onReorder,
	isRowDraggable,
}: SortableTasksTableBodyProps<T>) {
	const sortDragEnabled = orderBy === 'dateDue'

	const sensors = useSensors(
		useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
		useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
	)

	const groups = useMemo(() => groupTasksByDay(tasks), [tasks])

	const handleDragEnd = (event: DragEndEvent) => {
		const { active, over } = event
		if (!over || active.id === over.id) return
		const group = groups.find(g => g.tasks.some(t => t._id === active.id))
		if (!group) return
		const ids = group.tasks.map(t => t._id)
		const oldIndex = ids.indexOf(active.id as string)
		const newIndex = ids.indexOf(over.id as string)
		if (oldIndex < 0 || newIndex < 0) return
		const reorderedIds = arrayMove(ids, oldIndex, newIndex)
		onReorder(reorderedIds)
	}

	return (
		<DndContext
			sensors={sensors}
			collisionDetection={closestCenter}
			onDragEnd={handleDragEnd}
			// DndContext renders a screen-reader live region as a plain <div>.
			// Because this component sits between <table> and <tbody>, that div
			// landed directly inside <table>, which React rejects with
			// "validateDOMNesting: <div> cannot appear as a child of <table>".
			// Rendering the announcements into <body> keeps the accessibility
			// behaviour while producing valid table markup.
			accessibility={{
				container: typeof document !== 'undefined' ? document.body : undefined,
			}}
		>
			<TableBody>
				{groups.map(group => (
					<SortableContext
						key={group.dayKey}
						items={group.tasks.map(t => t._id)}
						strategy={verticalListSortingStrategy}
					>
						<TableRow className="bg-gray-50 hover:bg-gray-50 border-b border-gray-200">
							<TableCell
								colSpan={columnCount}
								className="py-1.5 px-3 text-[11px] font-semibold uppercase tracking-wide text-gray-500"
							>
								{group.dayLabel}
								<span className="ml-2 font-normal lowercase tracking-normal text-gray-400">
									{group.tasks.length} {group.tasks.length === 1 ? 'task' : 'tasks'}
								</span>
							</TableCell>
						</TableRow>
						{group.tasks.map(task => (
							<SortableTaskRow
								key={task._id}
								task={task}
								dragEnabled={sortDragEnabled && (isRowDraggable ? isRowDraggable(task) : true)}
								rowClassName={rowClassName?.(task)}
								onClick={onRowClick ? () => onRowClick(task) : undefined}
							>
								{renderRowCells(task)}
							</SortableTaskRow>
						))}
					</SortableContext>
				))}
			</TableBody>
		</DndContext>
	)
}

export default SortableTasksTableBody
